import { useState } from "react";
import { urlSAC } from "../lib/zentria";
import { Link, useParams, useNavigate } from "react-router-dom";
import ProductoImagen from "../components/catalogo/ProductoImagen.jsx";
import ProductoCard from "../components/catalogo/ProductoCard.jsx";
import { useProducto, useProductos, useCategorias } from "../lib/useCatalogo";
import { useCarrito } from "../lib/CarritoContext.jsx";
import { soles, numero } from "../lib/format";
import { etiqueta } from "../lib/etiquetas";
import { IGV } from "../lib/config";
import s from "./ProductoDetalle.module.css";

export default function ProductoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { producto, loading } = useProducto(id);
  const { productos } = useProductos();
  const { categorias } = useCategorias();
  const { agregar } = useCarrito();

  const [cantidad, setCantidad] = useState(1);
  const [agregado, setAgregado] = useState(false);

  if (loading) {
    return (
      <div className={`container ${s.estado}`}>
        <p className="mono">Cargando producto...</p>
      </div>
    );
  }

  if (!producto) {
    return (
      <div className={`container ${s.estado}`}>
        <h1 className={s.estadoTitulo}>Producto no encontrado</h1>
        <p>Puede que ya no este en catalogo o que el enlace sea antiguo.</p>
        <Link to="/productos" className="btn btn--accent">
          Volver al catalogo
        </Link>
      </div>
    );
  }

  const categoria = categorias.find(
    (c) => (c.slug || c.id) === producto.categoria
  );

  const relacionados = productos
    .filter((p) => p.categoria === producto.categoria && p.id !== producto.id)
    .slice(0, 4);

  const peso = producto.especificaciones?.pesoUnitario;
  const sinStock = typeof producto.stock === "number" && producto.stock <= 0;
  const subtotal = (producto.precio ?? 0) * cantidad;

  function alAgregar() {
    agregar(producto, cantidad);
    setAgregado(true);
    setTimeout(() => setAgregado(false), 2200);
  }

  return (
    <>
      <nav className={`container ${s.miga}`} aria-label="Ruta">
        <Link to="/">Inicio</Link>
        <span aria-hidden="true">/</span>
        <Link to="/productos">Catalogo</Link>
        {categoria && (
          <>
            <span aria-hidden="true">/</span>
            <Link to={`/productos?categoria=${categoria.slug || categoria.id}`}>
              {categoria.nombre}
            </Link>
          </>
        )}
      </nav>

      <div className={`container ${s.ficha}`}>
        <div className={s.visual}>
          <ProductoImagen producto={producto} alto={380} />
          {peso && (
            <p className={`mono ${s.pieVisual}`}>
              Peso teorico: {numero(peso, "kg")} por {producto.unidad}
            </p>
          )}
        </div>

        <div className={s.info}>
          <p className={`mono ${s.sku}`}>SKU {producto.sku}</p>
          <h1 className={s.nombre}>{producto.nombre}</h1>
          <p className={s.descripcion}>{producto.descripcion}</p>


          <div className={s.precioBloque}>
            <div>
              <span className={s.precio}>{soles(producto.precio)}</span>
              <span className={s.porUnidad}>por {producto.unidad}</span>
            </div>
            <p className="hint">
              Precio sin IGV. Con IGV: {soles((producto.precio ?? 0) * (1 + IGV))}
            </p>
          </div>

          <div className={s.stock}>
            {sinStock ? (
              <span className="chip chip--danger">Sin stock</span>
            ) : (
              <span className="chip chip--ok">
                {numero(producto.stock)} {producto.unidad} en almacen
              </span>
            )}
          </div>

          <div className={s.compra}>
            <div className={s.cantidadCtrl}>
              <button
                type="button"
                onClick={() => setCantidad((c) => Math.max(1, c - 1))}
                aria-label="Restar una unidad"
              >
                &minus;
              </button>
              <input
                type="number"
                min="1"
                className="mono"
                value={cantidad}
                onChange={(e) =>
                  setCantidad(Math.max(1, Math.floor(Number(e.target.value) || 1)))
                }
                aria-label="Cantidad"
              />
              <button
                type="button"
                onClick={() => setCantidad((c) => c + 1)}
                aria-label="Sumar una unidad"
              >
                +
              </button>
              <span className={s.unidadTexto}>{producto.unidad}</span>
            </div>

            <p className={s.subtotal}>
              Subtotal <strong className="mono">{soles(subtotal)}</strong>
            </p>
          </div>

          <div className={s.acciones}>
            <button
              type="button"
              className="btn btn--accent"
              onClick={alAgregar}
              disabled={sinStock}
            >
              {agregado ? "Agregado ✓" : "Agregar al carrito"}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                agregar(producto, cantidad);
                // Las cotizaciones se atienden en el portal SAC del cliente.
                window.open(urlSAC(), "_blank", "noopener,noreferrer");
              }}
            >
              Pedir cotizacion
            </button>
          </div>

          <table className={s.tablaSpecs}>
            <caption className="visually-hidden">
              Especificaciones tecnicas
            </caption>
            <tbody>
              {Object.entries(producto.especificaciones || {}).map(
                ([clave, valor]) => (
                  <tr key={clave}>
                    <th scope="row">{etiqueta(clave)}</th>
                    <td className="mono">
                      {clave === "pesoUnitario" ? numero(valor, "kg") : valor}
                    </td>
                  </tr>
                )
              )}
              <tr>
                <th scope="row">Unidad de venta</th>
                <td className="mono">{producto.unidad}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {relacionados.length > 0 && (
        <section className="section section--surface">
          <div className="container">
            <p className="eyebrow">Tambien de esta linea</p>
            <h2 className={s.tituloRelacionados}>Productos relacionados</h2>
            <div className={s.gridRelacionados}>
              {relacionados.map((p) => (
                <ProductoCard key={p.id} producto={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
